import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Wrench, Plus, RefreshCw, LayoutGrid, List,
  Clock, CheckCircle2, XCircle, AlertTriangle, Zap,
  Filter, Building2, ChevronRight,
} from "lucide-react";

const STATUSES = [
  { key: "solicitada", label: "Solicitadas", color: "border-slate-300 bg-slate-50" },
  { key: "cotizando", label: "En cotizacion", color: "border-blue-200 bg-blue-50" },
  { key: "cotizaciones_subidas", label: "Con cotizaciones", color: "border-yellow-200 bg-yellow-50" },
  { key: "cotizacion_aprobada", label: "Cotizacion aprobada", color: "border-purple-200 bg-purple-50" },
  { key: "pago_solicitado", label: "Pago solicitado", color: "border-orange-200 bg-orange-50" },
  { key: "pago_aprobado", label: "Pago aprobado", color: "border-green-200 bg-green-50" },
  { key: "en_tesoreria", label: "En tesoreria", color: "border-teal-200 bg-teal-50" },
  { key: "completada", label: "Completadas", color: "border-emerald-300 bg-emerald-50" },
];

const PRIORITY_CHIP = {
  baja: "bg-slate-100 text-slate-600",
  media: "bg-blue-100 text-blue-700",
  alta: "bg-orange-100 text-orange-700",
  urgente: "bg-red-100 text-red-700",
};

const CATEGORY_ICONS = {
  electrico: "⚡", plomeria: "🔧", refrigeracion: "❄", cocina: "🍳",
  limpieza_industrial: "🧹", estructural: "🏗", tecnologia: "💻",
  fumigacion: "🌿", otros: "🔩",
};

function fmtDate(iso) {
  if (!iso) return "--";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function RequestCard({ req, onClick }) {
  return (
    <div
      data-testid={`mnt-card-${req.id}`}
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-lg p-3 cursor-pointer hover:border-yellow-400 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="text-xs font-mono text-slate-500">{req.request_number}</span>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${PRIORITY_CHIP[req.priority] || "bg-slate-100 text-slate-600"}`}>
          {req.priority}
        </span>
      </div>
      <p className="text-sm font-semibold text-slate-800 leading-tight line-clamp-2 mb-1.5">{req.title}</p>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Building2 className="w-3 h-3" />
        <span>{req.branch_name || req.branch_id}</span>
      </div>
      {req.quotations?.length > 0 && (
        <div className="mt-1.5 text-xs text-green-700 font-medium">
          {req.quotations.length} cotizacion{req.quotations.length > 1 ? "es" : ""}
          {req.quotation_approved_id && " — aprobada"}
        </div>
      )}
      <div className="mt-1.5 text-xs text-slate-400">{fmtDate(req.requested_at)}</div>
    </div>
  );
}

export default function OpsMantenimientos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [filters, setFilters] = useState({ status: "", category: "", priority: "" });
  const [showFilters, setShowFilters] = useState(false);

  const canCreate = ["branch_manager", "mantenimiento", "corporate_admin", "operativo"].includes(user?.role);
  const isManagerOrMaint = ["mantenimiento", "corporate_admin", "treasury", "direccion"].includes(user?.role);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;
      const res = await api.get("/ops/maintenance/requests", { params });
      setRequests(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Kanban columns — filter open/closed separately
  const activeStatuses = STATUSES.filter(s => !["completada"].includes(s.key));
  const closedStatus = STATUSES.find(s => s.key === "completada");
  const rejectedRequests = requests.filter(r => r.status === "rechazada" || r.status === "cancelada");
  const completedRequests = requests.filter(r => r.status === "completada");

  function kanbanColumn(statusObj) {
    const items = requests.filter(r => r.status === statusObj.key);
    return (
      <div key={statusObj.key} className={`flex-shrink-0 w-56 rounded-xl border ${statusObj.color} p-3`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{statusObj.label}</span>
          <span className="text-xs font-semibold bg-white border border-slate-200 px-1.5 py-0.5 rounded-full text-slate-600">{items.length}</span>
        </div>
        <div className="space-y-2">
          {items.map(req => (
            <RequestCard key={req.id} req={req} onClick={() => navigate(`/ops/mantenimientos/${req.id}`)} />
          ))}
          {items.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">Sin solicitudes</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-yellow-500" />
            Mantenimientos
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestion de solicitudes y seguimiento de pagos</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadRequests}
            data-testid="mnt-refresh-btn"
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView(v => v === "kanban" ? "table" : "kanban")}
            data-testid="mnt-view-toggle"
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"
          >
            {view === "kanban" ? <List className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setShowFilters(f => !f)}
            data-testid="mnt-filters-btn"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showFilters ? "bg-yellow-50 border-yellow-300 text-yellow-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            <Filter className="w-4 h-4" />
            Filtros
          </button>
          {canCreate && (
            <button
              onClick={() => navigate("/ops/mantenimientos/nuevo")}
              data-testid="mnt-new-btn"
              className="flex items-center gap-1.5 px-4 py-2 bg-[#FFDF00] hover:bg-yellow-400 text-slate-900 font-semibold rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nueva solicitud
            </button>
          )}
        </div>
      </div>

      {/* Filtros rápidos */}
      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3">
          <select
            data-testid="mnt-filter-status"
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white"
          >
            <option value="">Todos los estados</option>
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            <option value="rechazada">Rechazadas</option>
            <option value="cancelada">Canceladas</option>
          </select>
          <select
            data-testid="mnt-filter-priority"
            value={filters.priority}
            onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white"
          >
            <option value="">Todas las prioridades</option>
            {["baja", "media", "alta", "urgente"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            data-testid="mnt-filter-category"
            value={filters.category}
            onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white"
          >
            <option value="">Todas las categorias</option>
            {["electrico","plomeria","refrigeracion","cocina","limpieza_industrial","estructural","tecnologia","fumigacion","otros"].map(c =>
              <option key={c} value={c}>{c}</option>
            )}
          </select>
          <button
            onClick={() => setFilters({ status: "", category: "", priority: "" })}
            className="text-sm text-slate-500 hover:text-slate-800 px-3 py-1.5 border border-slate-200 rounded-lg"
          >
            Limpiar
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Cargando...</div>
      ) : view === "kanban" ? (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {activeStatuses.map(s => kanbanColumn(s))}
          </div>
          {/* Completadas / rechazadas al pie */}
          <div className="mt-4 flex gap-3">
            {closedStatus && kanbanColumn(closedStatus)}
            <div className="flex-shrink-0 w-56 rounded-xl border border-red-100 bg-red-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Rechazadas / Canceladas</span>
                <span className="text-xs font-semibold bg-white border border-slate-200 px-1.5 py-0.5 rounded-full text-slate-600">{rejectedRequests.length}</span>
              </div>
              <div className="space-y-2">
                {rejectedRequests.slice(0, 5).map(req => (
                  <RequestCard key={req.id} req={req} onClick={() => navigate(`/ops/mantenimientos/${req.id}`)} />
                ))}
                {rejectedRequests.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Sin solicitudes</p>}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Vista tabla */
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Numero</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Titulo</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Sucursal</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Categoria</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Prioridad</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Estado</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Fecha</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(req => (
                <tr
                  key={req.id}
                  data-testid={`mnt-row-${req.id}`}
                  className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/ops/mantenimientos/${req.id}`)}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{req.request_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-800 max-w-xs">
                    <span className="line-clamp-1">{req.title}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{req.branch_name || req.branch_id}</td>
                  <td className="px-4 py-3 text-slate-600">{CATEGORY_ICONS[req.category]} {req.category}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PRIORITY_CHIP[req.priority] || ""}`}>
                      {req.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{req.status}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(req.requested_at)}</td>
                  <td className="px-4 py-3">
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">
                    No hay solicitudes con los filtros actuales
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
